<template>
  <v-dialog max-width="550px" v-model="dialog" @keydown.enter.prevent="save">
    <v-card>
      <v-card-title>
        <span class="headline">Search Config</span>
      </v-card-title>
      <v-card-text>
        <v-row dense class="mb-2">
          <template v-if="!model.useFilters">
            <v-col cols="12" sm="6" md="6">
              <v-checkbox v-model="model.major" class="mx-1 mt-0" label="Major search" hide-details/>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-checkbox v-model="model.minor" class="mx-1 mt-0" label="Minor search" hide-details/>
            </v-col>

            <v-col cols="12" sm="6" md="6">
              <v-checkbox v-model="model.skipLocked" class="mx-1 mt-0" label="Skip Locked" hide-details/>
            </v-col>
            <v-col cols="12" sm="6" md="6">
              <v-checkbox v-model="model.skipUnlocked" class="mx-1 mt-0" label="Skip Unlocked" hide-details/>
            </v-col>
          </template>
          
          <v-col cols="12" sm="12" md="12">
            <r-item-table :items.sync="model.sources" title="Sources"/>
          </v-col>
          
          <v-col cols="12">
            <v-checkbox v-model="model.useFilters" class="mx-1 mt-3" label="Use Advanced Filters" hide-details/>
          </v-col>
          
          <template v-if="model.useFilters">
            <v-col cols="12">
              <v-divider class="my-3"></v-divider>
              <h4 class="mb-3">Filter Tags</h4>
            </v-col>
          
          <v-col cols="12" sm="6" md="6">
            <v-card outlined class="pa-3">
              <v-card-subtitle class="pa-0 mb-2">Positive Filters</v-card-subtitle>
              <div class="d-flex flex-wrap mb-2">
                <v-chip 
                  v-for="(filter, index) in model.positiveFilters" 
                  :key="'pos-' + index"
                  close
                  small
                  @click:close="removePositiveFilter(index)"
                  class="ma-1"
                >
                  {{ getFilterLabel(filter, 'positive') }}
                </v-chip>
              </div>
              <div class="d-flex flex-wrap">
                <v-btn
                  v-for="option in positiveFilterOptions"
                  :key="'pos-btn-' + option.value"
                  :disabled="model.positiveFilters.includes(option.value)"
                  @click="addPositiveFilter(option.value)"
                  small
                  outlined
                  class="ma-1"
                >
                  {{ option.label }}
                </v-btn>
              </div>
            </v-card>
          </v-col>
          
          <v-col cols="12" sm="6" md="6">
            <v-card outlined class="pa-3">
              <v-card-subtitle class="pa-0 mb-2">Negative Filters</v-card-subtitle>
              <div class="d-flex flex-wrap mb-2">
                <v-chip 
                  v-for="(filter, index) in model.negativeFilters" 
                  :key="'neg-' + index"
                  close
                  small
                  color="error"
                  @click:close="removeNegativeFilter(index)"
                  class="ma-1"
                >
                  {{ getFilterLabel(filter, 'negative') }}
                </v-chip>
              </div>
              <div class="d-flex flex-wrap">
                <v-btn
                  v-for="option in negativeFilterOptions"
                  :key="'neg-btn-' + option.value"
                  :disabled="model.negativeFilters.includes(option.value)"
                  @click="addNegativeFilter(option.value)"
                  small
                  outlined
                  color="error"
                  class="ma-1"
                >
                  {{ option.label }}
                </v-btn>
              </div>
            </v-card>
          </v-col>
          </template>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>Cancel</v-btn>
        <v-btn @click="save" color="blue darken-1" text>Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import {ViewRules} from "@/common/ViewRules";
import {SearchConfig, POSITIVE_FILTERS, NEGATIVE_FILTERS} from "@/modules/redactor/models/search/SearchConfig";
import RItemTable from "@/modules/redactor/views/item/r-item-table";

export default {
  name: "r-search-config",
  components: {RItemTable},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: new SearchConfig(),
    rules: ViewRules.READONLY,
    panels: [],
    positiveFilterOptions: [
      { label: 'Include Major', value: POSITIVE_FILTERS.MAJOR },
      { label: 'Include Minor', value: POSITIVE_FILTERS.MINOR }
    ],
    negativeFilterOptions: [
      { label: 'Skip Locked', value: NEGATIVE_FILTERS.SKIP_LOCKED },
      { label: 'Skip Unlocked', value: NEGATIVE_FILTERS.SKIP_UNLOCKED }
    ]
  }),
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new SearchConfig();
      this.panels = [];
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.empty()) {
        this.resolve(null);
        this.discard();
      } else if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
    },
    addPositiveFilter(value) {
      if (value && !this.model.positiveFilters.includes(value)) {
        this.model.positiveFilters.push(value);
      }
    },
    removePositiveFilter(index) {
      this.model.positiveFilters.splice(index, 1);
    },
    addNegativeFilter(value) {
      if (value && !this.model.negativeFilters.includes(value)) {
        this.model.negativeFilters.push(value);
      }
    },
    removeNegativeFilter(index) {
      this.model.negativeFilters.splice(index, 1);
    },
    getFilterLabel(value, type = 'positive') {
      if (type === 'positive') {
        const positiveMap = {
          [POSITIVE_FILTERS.MAJOR]: 'Include Major',
          [POSITIVE_FILTERS.MINOR]: 'Include Minor'
        };
        return positiveMap[value] || 'Unknown';
      } else {
        const negativeMap = {
          [NEGATIVE_FILTERS.SKIP_LOCKED]: 'Skip Locked',
          [NEGATIVE_FILTERS.SKIP_UNLOCKED]: 'Skip Unlocked'
        };
        return negativeMap[value] || 'Unknown';
      }
    }
  }
}
</script>